import { BlobResponse, RemoteData, Resource } from 'app/infrastructure/remote-data';
import { FavouriteDefinition, ApiResponse, ComplexValue } from 'app/types-shared';
import { Metadata, ScheduleDefinition } from 'app/area/company-report/types-store';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import { CodetableWireFormat } from 'app/infrastructure/ui/form/types-codetable';

export const endpoint = {
  favouriteDefinition: 'favourite/definition/draft',
  favouriteDefinitionTemplateSpecialReport: 'favourite/definition/draft/template/specialreport',
  specialReportExecute: 'specialreport',
  specialReportMetadata: (id: number | string) => `specialReport/${id}/metadata`,
  filterDomainsSpecialReport: (companyReportId: number) => `report/specialReport/${companyReportId}/filterDomains`,
  specialReportTemplate: (id: number | string) => `specialReport/${id}/template`,
  deleteSchedule: (favouriteId: number, scheduleDefinitionId: number) => `favourite/definition/draft/${favouriteId}/schedule/${scheduleDefinitionId}`,
  scheduleDefinition: (favouriteId: number) => `favourite/definition/draft/${favouriteId}/schedules`,
  specialReportScheduleRecurrencePatternOptions: 'favourite/definition/draft/schedule/recurrencepattern/options',
  specialReportScheduleWeekdayOptions: 'favourite/definition/draft/schedule/weekday/options',
  specialReportScheduleMonthOptions: 'favourite/definition/draft/schedule/month/options',
  specialReportScheduleRecurrenceOccurences: 'favourite/definition/draft/schedule/recurrenceoccurrence/options'
};

export const api = {

  loadFavouriteDefinitionTemplate: async (): Promise<number> => {

    const response = await new RemoteData(endpoint.favouriteDefinitionTemplateSpecialReport)
      .resource<any>()
      .create(undefined, false);

    const json = await response.json();

    return json.data;
  },

  loadMetadata: async (id: number): Promise<any> => {
    const response = await new RemoteData(endpoint.specialReportMetadata(id))
      .resource<ApiResponse<Metadata>>()
      .single();

    return response.data;
  },

  definition: async (id: string | number): Promise<FavouriteDefinition> => {

    const response = await new RemoteData(endpoint.favouriteDefinition).resource<ApiResponse<FavouriteDefinition>>()
      .withId(id)
      .single();

    return response.data;
  },

  sendReport: async (favouriteDefinition: FavouriteDefinition): Promise<ApiResponse<FavouriteDefinition>> => {
    return await new RemoteData(endpoint.specialReportExecute)
      .resource<ApiResponse<FavouriteDefinition>>()
      .create(favouriteDefinition, false);
  },

  deleteSchedule: async (favouriteId: number, scheduleDefinitionId: number) => {
    return await new RemoteData(endpoint.deleteSchedule(favouriteId, scheduleDefinitionId))
      .resource<ApiResponse<any>>()
      .httpDelete(scheduleDefinitionId);
  },

  getScheduleDefinition: async (favouriteId: number): Promise<ScheduleDefinition> => {
    const response = await new RemoteData(endpoint.scheduleDefinition(favouriteId))
    .resource<ApiResponse<ScheduleDefinition>>()
    .single();

    return response.data?.[0];
  },

  saveScheduleDefinition: async (
    scheduleDefinition: any,
    favourieId: number,
    includeFailedConstraints: boolean = false
  ): Promise<any> => {
    const response = await new RemoteData(endpoint.scheduleDefinition(favourieId))
      .resource<any>()
      .filter(includeFailedConstraints && {includeFailedConstraints})
      .save(scheduleDefinition);

    return response.json?.data?.[0]?.metaData?.failedConstraints;
  },

  createScheduleDefinition: async (
    favourieId: number
  ): Promise<any> => {
    const response = await new RemoteData(endpoint.scheduleDefinition(favourieId))
      .resource<any>()
      .create(undefined, false);

    const json = await response.json();  

    return json?.data?.[0];
  },

  loadUsePredefDate: async (companyReportId?: number): Promise<boolean> => {
    const response = await new RemoteData(endpoint.filterDomainsSpecialReport(companyReportId)) 
      .resource<ApiResponse<any>>()
      .all();

    return response.data.usePredefDate;
  },

  downloadTemplate: async (reportId: number | string): Promise<Resource<BlobResponse>> => {
    return await new RemoteData(endpoint.specialReportTemplate(reportId))
      .resource<BlobResponse>();
  },

  getRecurrencePatternOptions: async () => {
    const response: any = await new RemoteData(endpoint.specialReportScheduleRecurrencePatternOptions)
      .resource<ApiResponse<any>>()
      .filter({displayNotSelected: false})
      .single();

    return response.data?.rootCodes?.map((code: CodetableWireFormat): ComplexValue<number> => codeItemToStoreValue(code));
  },

  getScheduleWeekDays: async () => {
    const response: any = await new RemoteData(endpoint.specialReportScheduleWeekdayOptions)
      .resource<ApiResponse<any>>()
      .filter({displayNotSelected: false})
      .single();

    return response.data?.rootCodes?.map((code: CodetableWireFormat): ComplexValue<number> => codeItemToStoreValue(code));
  },

  getScheduleMonths: async () => {
    const response: any = await new RemoteData(endpoint.specialReportScheduleMonthOptions)
      .resource<ApiResponse<any>>()
      .filter({displayNotSelected: false})
      .single();

    return response.data?.rootCodes?.map((code: CodetableWireFormat): ComplexValue<number> => codeItemToStoreValue(code));
  },

  getScheduleOccurences: async () => {
    const response: any = await new RemoteData(endpoint.specialReportScheduleRecurrenceOccurences)
      .resource<ApiResponse<any>>()
      .filter({displayNotSelected: false})
      .single();

    return response.data?.rootCodes?.map((code: CodetableWireFormat): ComplexValue<number> => codeItemToStoreValue(code));
  }
};
